import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";
import { Shield, ChevronRight, ChevronDown, Phone, Mail, MapPin } from "lucide-react";

const STATUS_TONES = {
    submitted: "bg-amber-100 text-amber-800",
    contacted: "bg-blue-100 text-blue-700",
    converted: "bg-emerald-100 text-emerald-700",
    rejected: "bg-zinc-100 text-zinc-600",
};
const TABS = ["all", "submitted", "contacted", "converted", "rejected"];

const AdminInsuranceLeads = () => {
    const [tab, setTab] = useState("submitted");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (tab !== "all") params.set("status", tab);
        api.get(`/admin/insurance-leads?${params.toString()}`).then((r) => {
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        }).finally(() => setLoading(false));
    };
    useEffect(load, [tab]); // eslint-disable-line

    const updateLead = async (id, body) => {
        try {
            await api.patch(`/admin/insurance-leads/${id}`, body);
            toast.success("Lead updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <AdminAuthGuard>
            <AdminLayout title="Insurance leads" subtitle={`${total} lead(s) in this view`}>
                <div className="mb-4 flex flex-wrap gap-1.5" data-testid="admin-insurance-tabs">
                    {TABS.map((s) => (
                        <button
                            key={s}
                            onClick={() => { setTab(s); setOpenId(null); }}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${tab === s ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:bg-white/5 dark:text-zinc-300"}`}
                            data-testid={`admin-insurance-tab-${s}`}
                        >
                            {s.replace("_", " ")}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <p className="text-sm text-zinc-500">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">No leads in this view.</p>
                ) : (
                    <ul className="space-y-2" data-testid="admin-insurance-list">
                        {items.map((l) => (
                            <li key={l.id} className="rounded-2xl border border-zinc-100 bg-white" data-testid={`admin-insurance-row-${l.id}`}>
                                <button
                                    onClick={() => setOpenId((o) => o === l.id ? null : l.id)}
                                    className="flex w-full items-center gap-3 p-4 text-left"
                                >
                                    <Shield className="h-4 w-4 text-brand-700" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-zinc-900">{l.applicant?.full_name} <span className="text-xs text-zinc-500">· {l.plan_name}</span></p>
                                        <p className="mt-0.5 text-xs text-zinc-500 line-clamp-1">{l.plan_provider} · prefers {l.preferred_call_time} · submitted {new Date(l.submitted_at).toLocaleString()}</p>
                                    </div>
                                    <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_TONES[l.status] || "bg-zinc-100 text-zinc-700"}`}>{l.status}</span>
                                    {openId === l.id ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                                </button>
                                {openId === l.id && (
                                    <div className="border-t border-zinc-100 p-4" data-testid={`admin-insurance-expand-${l.id}`}>
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div>
                                                <p className="text-xs font-medium text-zinc-500">Applicant</p>
                                                <p className="mt-1 text-sm font-medium text-zinc-900">{l.applicant?.full_name}</p>
                                                <p className="text-xs text-zinc-500">DOB: {l.applicant?.dob} · {l.applicant?.gender}</p>
                                                <p className="mt-1 text-xs text-zinc-700"><Phone className="mr-1 inline h-3 w-3" />{l.applicant?.phone}</p>
                                                <p className="text-xs text-zinc-700"><Mail className="mr-1 inline h-3 w-3" />{l.applicant?.email}</p>
                                                <p className="text-xs text-zinc-700"><MapPin className="mr-1 inline h-3 w-3" />Pincode {l.applicant?.pincode}</p>
                                                {(l.applicant?.family_members || []).length > 0 && (
                                                    <div className="mt-2">
                                                        <p className="text-xs font-medium text-zinc-500">Family ({l.applicant.family_members.length})</p>
                                                        <ul className="mt-1 space-y-0.5 text-xs text-zinc-600">
                                                            {l.applicant.family_members.map((m, i) => (<li key={i}>{m.name} · {m.relation} · DOB {m.dob}</li>))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-zinc-500">Lead actions</p>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    {["contacted", "converted", "rejected"].map((s) => (
                                                        <button key={s} onClick={() => updateLead(l.id, { status: s })} disabled={l.status === s} className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs hover:bg-zinc-50 disabled:opacity-40" data-testid={`admin-insurance-set-${s}-${l.id}`}>
                                                            Mark {s}
                                                        </button>
                                                    ))}
                                                </div>
                                                <textarea
                                                    defaultValue={l.admin_notes || ""}
                                                    placeholder="Admin notes — call log, follow-ups…"
                                                    rows={4}
                                                    maxLength={2000}
                                                    className="mt-3 w-full rounded-md border border-zinc-200 px-3 py-2 text-xs focus:border-brand-400 focus:outline-none"
                                                    onBlur={(e) => {
                                                        const v = e.target.value;
                                                        if (v !== (l.admin_notes || "")) updateLead(l.id, { admin_notes: v });
                                                    }}
                                                    data-testid={`admin-insurance-notes-${l.id}`}
                                                />
                                                <p className="mt-1 text-[10px] text-zinc-400">Notes save on blur.</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminInsuranceLeads;
